import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useEdgesState,
  useNodesState,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFloppyDisk,
  faUpload,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  getBotDefinition,
  getBotRunLogs,
  listMetaFlowScreens,
  listMetaFlows,
  publishBotDefinition,
  updateBotDefinition,
  validateBotDefinition,
} from '../../../lib/api';
import '../../admin/pages/admin.css';
import '../styles/bot-builder.css';
import { PALETTE, defaultDataForType, nodeTypes } from '../components/BotNodes';

const toFlowNodes = (graph) =>
  (graph?.nodes || []).map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position || { x: 100, y: 100 },
    data: { ...(n.data || {}), label: n.data?.label || n.type },
  }));

const toFlowEdges = (graph) =>
  (graph?.edges || []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle || undefined,
    targetHandle: e.targetHandle || undefined,
    label: e.label,
    markerEnd: { type: MarkerType.ArrowClosed },
  }));

const fromFlow = (nodes, edges) => ({
  nodes: nodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position,
    data: n.data || {},
  })),
  edges: edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle || null,
    targetHandle: e.targetHandle || null,
    label: e.label,
  })),
});

const BotBuilderEditor = () => {
  const { id } = useParams();
  const [bot, setBot] = useState(null);
  const [name, setName] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [validation, setValidation] = useState(null);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedId) || null,
    [nodes, selectedId]
  );

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await getBotDefinition(id);
      const data = res?.data;
      setBot(data);
      setName(data?.name || '');
      setNodes(toFlowNodes(data?.graph_json));
      setEdges(toFlowEdges(data?.graph_json));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load bot');
    }
  }, [id, setNodes, setEdges]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await getBotRunLogs(id, 40);
      setLogs(res?.data || []);
    } catch {
      setLogs([]);
    }
  }, [id]);

  useEffect(() => {
    load();
    loadLogs();
  }, [load, loadLogs]);

  const onConnect = useCallback(
    (connection) =>
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: `e_${connection.source}_${connection.sourceHandle || 'default'}_${connection.target}_${Date.now()}`,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          eds
        )
      ),
    [setEdges]
  );

  const addNode = (type) => {
    const nid = `${type}_${Date.now()}`;
    setNodes((nds) => [
      ...nds,
      {
        id: nid,
        type,
        position: { x: 180 + nds.length * 20, y: 100 + nds.length * 30 },
        data: defaultDataForType(type),
      },
    ]);
    setSelectedId(nid);
  };

  const updateSelectedData = (patch) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n
      )
    );
  };

  const onSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const graph_json = fromFlow(nodes, edges);
      const res = await updateBotDefinition(id, { name, graph_json });
      setBot(res?.data);
      setMessage('Saved (draft). Publish to activate on your WhatsApp number.');
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          (e?.response?.data?.errors || []).join(', ') ||
          e?.message ||
          'Save failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const onValidate = async () => {
    setError('');
    try {
      await onSave();
      const res = await validateBotDefinition(id);
      setValidation(res);
      if (res?.ok) setMessage('Graph looks valid.');
      else setError((res?.errors || []).join('; '));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Validate failed');
    }
  };

  const onPublish = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await onSave();
      const res = await publishBotDefinition(id);
      setBot(res?.data);
      setMessage('Published. Inbound messages on your number will run this bot.');
      await loadLogs();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          (e?.response?.data?.errors || []).join(', ') ||
          e?.message ||
          'Publish failed'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Link to="/main/bot-builder" className="admin-btn admin-btn--ghost admin-btn--sm">
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Link>
            <div>
              <h1 className="admin-page__title" style={{ margin: 0 }}>Edit bot</h1>
              <p className="admin-page__subtitle" style={{ margin: 0 }}>
                Status: <span className={`bot-status bot-status--${bot?.status || 'draft'}`}>{bot?.status || '…'}</span>
                {bot?.version != null ? ` · v${bot.version}` : ''}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={loadLogs}>
              <FontAwesomeIcon icon={faRotateRight} /> Logs
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onValidate} disabled={saving}>
              Validate
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onSave} disabled={saving}>
              <FontAwesomeIcon icon={faFloppyDisk} /> Save
            </button>
            <button type="button" className="admin-btn admin-btn--primary" onClick={onPublish} disabled={saving}>
              <FontAwesomeIcon icon={faUpload} /> Publish
            </button>
          </div>
        </div>
      </header>

      {error && <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 12 }}>{error}</div>}
      {message && <div className="admin-feedback admin-feedback--success" style={{ marginBottom: 12 }}>{message}</div>}

      <div className="bot-builder-layout">
        <aside className="bot-palette">
          <h3>Nodes</h3>
          {PALETTE.map((p) => (
            <button key={p.type} type="button" onClick={() => addNode(p.type)}>
              {p.label}
            </button>
          ))}
        </aside>

        <div className="bot-canvas-wrap">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onSelectionChange={({ nodes: sel }) => setSelectedId(sel?.[0]?.id || null)}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        <aside className="bot-inspector">
          <h3>Properties</h3>
          <label>Bot name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />

          {!selectedNode ? (
            <p style={{ marginTop: 12, fontSize: 13, color: '#64748b' }}>
              Select a node to edit. Connect handles to build the path. For list/button rows, use the bottom handles (`row:ID`).
            </p>
          ) : (
            <NodeInspector node={selectedNode} onChange={updateSelectedData} />
          )}

          <h3 style={{ marginTop: 16 }}>Run logs</h3>
          <div className="bot-logs">
            {logs.length === 0 ? (
              <div>No logs yet.</div>
            ) : (
              logs.map((l) => (
                <div key={l.id} style={{ marginBottom: 6 }}>
                  [{new Date(l.created_at).toLocaleTimeString()}] {l.event} {l.node_id || ''}
                </div>
              ))
            )}
          </div>
          {validation && !validation.ok && (
            <ul style={{ fontSize: 12, color: '#b91c1c' }}>
              {(validation.errors || []).map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
};

const WaFlowFields = ({ data, onChange }) => {
  const [flows, setFlows] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [loadingScreens, setLoadingScreens] = useState(false);
  const [metaError, setMetaError] = useState('');
  const [showDrafts, setShowDrafts] = useState(false);

  const loadFlows = useCallback(async (publishedOnly) => {
    setLoadingFlows(true);
    setMetaError('');
    try {
      const res = await listMetaFlows(publishedOnly);
      setFlows(res?.data || []);
    } catch (e) {
      setMetaError(
        e?.response?.data?.message || e?.message || 'Failed to load flows from Meta'
      );
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  useEffect(() => {
    loadFlows(!showDrafts);
  }, [loadFlows, showDrafts]);

  useEffect(() => {
    const flowId = data.flow_id;
    if (!flowId) {
      setScreens([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingScreens(true);
      try {
        const res = await listMetaFlowScreens(flowId);
        if (!cancelled) setScreens(res?.data || []);
      } catch {
        if (!cancelled) setScreens([]);
      } finally {
        if (!cancelled) setLoadingScreens(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.flow_id]);

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <button
          type="button"
          className="admin-btn admin-btn--ghost admin-btn--sm"
          onClick={() => loadFlows(!showDrafts)}
          disabled={loadingFlows}
        >
          {loadingFlows ? 'Loading…' : 'Refresh from Meta'}
        </button>
        <label style={{ fontSize: 12, margin: 0 }}>
          <input
            type="checkbox"
            checked={showDrafts}
            onChange={(e) => setShowDrafts(e.target.checked)}
          />{' '}
          Include drafts
        </label>
      </div>
      {metaError && (
        <p style={{ fontSize: 12, color: '#b91c1c', margin: '0 0 8px' }}>{metaError}</p>
      )}

      <label>Flow (from Meta)</label>
      <select
        value={data.flow_id || ''}
        onChange={(e) => {
          const id = e.target.value;
          const selected = flows.find((f) => f.id === id);
          onChange({
            flow_id: id,
            flow_name: selected?.name || data.flow_name || '',
            screen: '',
          });
        }}
      >
        <option value="">Select a flow…</option>
        {flows.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name} ({f.status}) — {f.id}
          </option>
        ))}
      </select>

      <label>Flow ID (manual override)</label>
      <input
        value={data.flow_id || ''}
        onChange={(e) => onChange({ flow_id: e.target.value, screen: data.screen || '' })}
        placeholder="Or paste Flow ID"
      />

      <label>Screen ID {loadingScreens ? '(loading…)' : ''}</label>
      {screens.length > 0 ? (
        <select
          value={data.screen || ''}
          onChange={(e) => onChange({ screen: e.target.value })}
        >
          <option value="">Select screen…</option>
          {screens.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      ) : null}
      <input
        value={data.screen || ''}
        onChange={(e) => onChange({ screen: e.target.value })}
        placeholder="Or paste screen id"
      />

      <label>CTA</label>
      <input
        value={data.flow_cta || ''}
        onChange={(e) => onChange({ flow_cta: e.target.value })}
      />
      <label>flow_type (saved to flow_responses)</label>
      <input
        value={data.flow_type || ''}
        onChange={(e) => onChange({ flow_type: e.target.value })}
        placeholder="e.g. feedback, complaints, callback, orderstatus"
      />
      <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
        On form submit, response is stored in flow_responses. LensHut types
        (feedback / complaints / callback / orderstatus) also trigger store emails.
        Later messages can use form fields as {"{{name}}"}, {"{{number}}"}, {"{{order_number}}"}.
      </p>
    </>
  );
};

const NodeInspector = ({ node, onChange }) => {
  const data = node.data || {};
  const type = node.type;

  return (
    <div>
      <p style={{ fontSize: 12, color: '#64748b' }}>Node: {node.id} ({type})</p>
      <label>Label</label>
      <input value={data.label || ''} onChange={(e) => onChange({ label: e.target.value })} />

      {(type === 'text' || type === 'end' || type === 'list' || type === 'buttons' || type === 'wa_flow') && (
        <>
          <label>Body / message</label>
          <textarea
            rows={3}
            value={data.body || data.text || ''}
            onChange={(e) => onChange({ body: e.target.value })}
          />
        </>
      )}

      {type === 'trigger' && (
        <>
          <label>Keywords (comma-separated)</label>
          <input
            value={(data.keywords || []).join(', ')}
            onChange={(e) =>
              onChange({
                keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              })
            }
          />
          <label>
            <input
              type="checkbox"
              checked={Boolean(data.matchAnyText)}
              onChange={(e) => onChange({ matchAnyText: e.target.checked })}
            />{' '}
            Match any text
          </label>
        </>
      )}

      {type === 'buttons' && (
        <>
          <label>Buttons JSON</label>
          <textarea
            rows={5}
            value={JSON.stringify(data.buttons || [], null, 2)}
            onChange={(e) => {
              try {
                onChange({ buttons: JSON.parse(e.target.value) });
              } catch {
                /* ignore while typing */
              }
            }}
          />
        </>
      )}

      {type === 'list' && (
        <>
          <label>List button label</label>
          <input value={data.button || ''} onChange={(e) => onChange({ button: e.target.value })} />
          <label>Sections JSON</label>
          <textarea
            rows={8}
            value={JSON.stringify(data.sections || [], null, 2)}
            onChange={(e) => {
              try {
                onChange({ sections: JSON.parse(e.target.value) });
              } catch {
                /* ignore */
              }
            }}
          />
        </>
      )}

      {type === 'condition' && (
        <>
          <label>Field (lastReplyId / lastReply / vars.x)</label>
          <input value={data.field || ''} onChange={(e) => onChange({ field: e.target.value })} />
          <label>Equals</label>
          <input value={data.equals ?? ''} onChange={(e) => onChange({ equals: e.target.value })} />
        </>
      )}

      {type === 'delay' && (
        <>
          <label>Seconds</label>
          <input
            type="number"
            min={1}
            value={data.seconds || 1}
            onChange={(e) => onChange({ seconds: Number(e.target.value) })}
          />
        </>
      )}

      {type === 'media' && (
        <>
          <label>Media type</label>
          <select
            value={data.mediaType || 'image'}
            onChange={(e) => onChange({ mediaType: e.target.value })}
          >
            <option value="image">image</option>
            <option value="video">video</option>
            <option value="document">document</option>
            <option value="audio">audio</option>
          </select>
          <label>Link URL</label>
          <input value={data.link || ''} onChange={(e) => onChange({ link: e.target.value })} />
          <label>Caption</label>
          <input value={data.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} />
        </>
      )}

      {type === 'wa_flow' && (
        <WaFlowFields data={data} onChange={onChange} />
      )}

      {type === 'http' && (
        <>
          <label>Method</label>
          <select value={data.method || 'POST'} onChange={(e) => onChange({ method: e.target.value })}>
            <option>GET</option>
            <option>POST</option>
            <option>PUT</option>
          </select>
          <label>URL</label>
          <input value={data.url || ''} onChange={(e) => onChange({ url: e.target.value })} />
          <label>Save response as</label>
          <input value={data.saveAs || 'http'} onChange={(e) => onChange({ saveAs: e.target.value })} />
          <label>Body template (JSON)</label>
          <textarea
            rows={4}
            value={typeof data.bodyTemplate === 'string' ? data.bodyTemplate : JSON.stringify(data.bodyTemplate || {}, null, 2)}
            onChange={(e) => onChange({ bodyTemplate: e.target.value })}
          />
        </>
      )}

      {type === 'email' && (
        <>
          <label>Recipient mode</label>
          <select
            value={
              data.recipientMode ||
              (data.lookupStoreEmail ? 'store' : 'fixed')
            }
            onChange={(e) =>
              onChange({
                recipientMode: e.target.value,
                lookupStoreEmail: e.target.value === 'store',
              })
            }
          >
            <option value="fixed">Fixed To (required below)</option>
            <option value="store">Store email from form store_name (LensHut)</option>
          </select>
          <label>To * (required unless Store mode)</label>
          <input
            value={data.to || ''}
            onChange={(e) => onChange({ to: e.target.value })}
            placeholder="ops@company.com"
          />
          <label>Subject *</label>
          <input
            value={data.subject || ''}
            onChange={(e) => onChange({ subject: e.target.value })}
            placeholder="Required — set on this bot"
          />
          <label>Body HTML * (use {"{{flow_fields_html}}"} for form table)</label>
          <textarea
            rows={8}
            value={data.body || ''}
            onChange={(e) => onChange({ body: e.target.value })}
            placeholder="Required — full HTML email body"
          />
          <label>
            <input
              type="checkbox"
              checked={Boolean(data.useLenshutMailer)}
              onChange={(e) => onChange({ useLenshutMailer: e.target.checked })}
            />{' '}
            From = LensHut mailer (LENSHUT_MAIL_USER); else platform support@
          </label>
          <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
            Code only provides the From SMTP account. To / Subject / Body must be set on the bot.
            Logs: <code>email_sent</code> shows resolved To.
          </p>
        </>
      )}

      {type === 'set_var' && (
        <>
          <label>Key</label>
          <input value={data.key || ''} onChange={(e) => onChange({ key: e.target.value })} />
          <label>Value</label>
          <input value={data.value || ''} onChange={(e) => onChange({ value: e.target.value })} />
        </>
      )}
    </div>
  );
};

export default BotBuilderEditor;
